import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { FeedbackNotification, Spinner } from '@toqio/toqio-web-components';
import { useBridge } from '../../bridge/useBridge';
import { useFlowReady } from '../../bridge/useFlowReady';
import { PageContainer, PrimaryButton } from '../../shared/components';
import { BalanceCard } from './components/BalanceCard';
import { formatAmount, MOBILE } from './components/styles';
import { useBalanceDashboard } from './useBalanceDashboard';

/**
 * un-custom-dashboard — a UN employee's read-only "Available Balance" widget.
 *
 * A single data-driven screen (no fields, no wizard): it fetches the balance on
 * entry behind a Spinner, then paints one card with the available balance.
 * Bypasses `StepWizard` (LESSONS § "skip StepWizard on flows with independent
 * entry points") and is `readiness: 'manual'` — it signals readiness once the
 * first load settles so its internal Spinner is never revealed.
 *
 * See `flows/UN-custom-dashboard/PRD.md` and `API_CONTRACT.md`.
 */
const FLOW_ID = 'un-custom-dashboard';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px 0; /* horizontal gutter comes from PageContainer (.lum-web-container) */

  @media (max-width: ${MOBILE}) {
    padding: 16px 0;
  }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 56px 24px;
  min-height: 50vh;
`;

export const UnCustomDashboardFlow: React.FC = () => {
  const bridge = useBridge();
  const { signalReady } = useFlowReady();
  const locale = bridge.locale ?? 'en';

  const { phase, data, error, reload } = useBalanceDashboard();

  // Emit step:change once on mount (bypassing StepWizard — it is its own
  // standalone screen).
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'balance',
      stepIndex: 0,
      totalSteps: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal → leave the embed.
  useEffect(
    () => bridge.onBackRequest(() => bridge.emit({ type: 'cancel', flowId: FLOW_ID })),
    [bridge],
  );

  // Manual readiness: drop the host overlay once the first load settles
  // (success or error), never revealing the internal Spinner.
  useEffect(() => {
    if (phase === 'loading') return;
    signalReady();
  }, [phase, signalReady]);

  if (phase === 'loading') {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  if (phase === 'error') {
    // 403 FORBIDDEN is non-retryable — the user lacks the authority, so a retry
    // can't help (PRD §9 / O-2). Other failures get a Retry.
    const forbidden = error?.status === 403;
    return (
      <PageContainer>
        <Root>
          <FeedbackNotification
            variant="error"
            title={forbidden ? t`Access not available` : t`Couldn't load your balance`}
            message={
              forbidden
                ? t`You don't have permission to view this balance.`
                : t`Something went wrong while loading your available balance. Please try again.`
            }
            bottomActions={
              forbidden ? undefined : (
                <PrimaryButton onClick={reload}>
                  <Trans>Try again</Trans>
                </PrimaryButton>
              )
            }
          />
        </Root>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Root>
        <BalanceCard
          formattedBalance={formatAmount(data?.availableBalance ?? 0, locale)}
          currency={data?.assetType ?? ''}
          accountId={data?.accountId}
        />
      </Root>
    </PageContainer>
  );
};

export default UnCustomDashboardFlow;
